import React, { useState, useEffect, useCallback, useMemo, useRef, createContext, useContext } from 'react';
import * as financeApi from '../finance/financeApi';
import ScheduleForm, {
  buildSchedulePayload,
  scheduleFromApi,
  todayYmd,
} from '../finance/ScheduleForm';
import './Finance.css';
import PaymentOverview from '../finance/PaymentOverview';
import { useDialogFocus } from '../hooks/useDialogFocus';

const FinanceFeedbackContext = createContext('');

const TABS = [
  { id: 'overview', label: 'Предстоящие' },
  { id: 'accounts', label: 'Счета' },
  { id: 'items', label: 'Регулярные' },
  { id: 'occurrences', label: 'Сверка' },
  { id: 'ledger', label: 'Журнал' },
];

const KIND_LABELS = {
  topup: 'Пополнение',
  debit_scheduled: 'Списание (план)',
  debit_confirmed: 'Списание (факт)',
  adjustment: 'Корректировка',
  migrated: 'Миграция',
};

const MUTABLE_KINDS = new Set(['topup', 'debit_confirmed', 'adjustment', 'migrated']);

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + (iso.length === 10 ? 'T12:00:00' : ''));
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatMoney(val, currency = 'RUB') {
  if (val == null || val === '') return '—';
  const n = Number(val);
  const formatted = Number.isFinite(n)
    ? n.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    : String(val);
  return `${formatted} ${currency}`;
}

function Modal({ title, onClose, children, wide }) {
  const dialog = useRef(null);
  const feedback = useContext(FinanceFeedbackContext);
  useDialogFocus(dialog, onClose);
  return (
    <div className="Finance-modalBackdrop" role="presentation" onClick={onClose}>
      <div
        className={`Finance-modal ${wide ? 'Finance-modal--wide' : ''}`}
        ref={dialog}
        role="dialog"
        aria-label={title}
        tabIndex={-1}
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="Finance-modalHeader">
          <h3>{title}</h3>
          <button type="button" className="Finance-iconBtn" onClick={onClose} aria-label="Закрыть">
            ×
          </button>
        </div>
        <div className="Finance-modalBody">{feedback && <div role="alert" className="Finance-banner Finance-banner--error">{feedback}</div>}{children}</div>
      </div>
    </div>
  );
}

function formatYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function Finance() {
  const [tab, setTab] = useState('overview');
  const [accounts, setAccounts] = useState([]);
  const [items, setItems] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [ledgerStart, setLedgerStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return formatYmd(d);
  });
  const [ledgerEnd, setLedgerEnd] = useState(() => formatYmd(new Date()));
  const [ledgerAccountFilter, setLedgerAccountFilter] = useState('');
  const [itemsAccountFilter, setItemsAccountFilter] = useState('');
  const [itemsGroupFilter, setItemsGroupFilter] = useState('');
  const [showDisabledItems, setShowDisabledItems] = useState(false);

  const [modal, setModal] = useState(null);

  const accountsById = useMemo(() => {
    const m = {};
    accounts.forEach((a) => { m[a.id] = a; });
    return m;
  }, [accounts]);

  const itemsById = useMemo(() => {
    const m = {};
    items.forEach((i) => { m[i.id] = i; });
    return m;
  }, [items]);

  const enabledItems = useMemo(() => items.filter((i) => i.enabled), [items]);
  const itemGroups = useMemo(() => [...new Set(items.map((i) => i.group_name).filter(Boolean))].sort(), [items]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [acc, it, led, occ] = await Promise.all([
        financeApi.fetchAccounts(),
        financeApi.fetchItems(),
        financeApi.fetchLedger({
          startDate: ledgerStart,
          endDate: ledgerEnd,
          accountId: ledgerAccountFilter || undefined,
          limit: 300,
        }),
        financeApi.fetchOccurrences(),
      ]);
      setAccounts(acc);
      setItems(it);
      setLedger(led);
      setOccurrences(occ);
    } catch (e) {
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [ledgerStart, ledgerEnd, ledgerAccountFilter]);

  const loadLedger = useCallback(async () => {
    try {
      const led = await financeApi.fetchLedger({
        startDate: ledgerStart,
        endDate: ledgerEnd,
        accountId: ledgerAccountFilter || undefined,
        limit: 300,
      });
      setLedger(led);
    } catch (e) {
      setError(e.message || 'Ошибка журнала');
    }
  }, [ledgerStart, ledgerEnd, ledgerAccountFilter]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const flash = (msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(null), 4000);
  };

  const runAction = async (fn, okMsg) => {
    setError(null);
    try {
      await fn();
      flash(okMsg);
      setModal(null);
      await loadAll();
    } catch (e) {
      setError(e.message);
    }
  };

  const filteredItems = useMemo(() => {
    let list = items;
    if (itemsAccountFilter) {
      list = list.filter((i) => String(i.account_id) === String(itemsAccountFilter));
    }
    if (itemsGroupFilter) list = list.filter((i) => i.group_name === itemsGroupFilter);
    if (!showDisabledItems) list = list.filter((i) => i.enabled);
    return list;
  }, [items, itemsAccountFilter, itemsGroupFilter, showDisabledItems]);

  return (
    <FinanceFeedbackContext.Provider value={error}><div className="Finance">
      <header className="Finance-header">
        <div className="Finance-headerMain">
          <h2 className="Finance-title">Финансы</h2>
          <p className="Finance-subtitle">Обязательные платежи и предоплаченные балансы</p>
        </div>
        <div className="Finance-headerActions">
          <button type="button" className="Finance-btn Finance-btn--ghost" onClick={loadAll} disabled={loading}>
            Обновить
          </button>
          <button
            type="button"
            className="Finance-btn"
            onClick={() => setModal({ type: 'item-create' })}
          >
            + Регулярный платёж
          </button>
        </div>
      </header>

      {error && <div className="Finance-banner Finance-banner--error">{error}</div>}
      {success && <div className="Finance-banner Finance-banner--ok">{success}</div>}

      <nav className="Finance-tabs" aria-label="Разделы финансов">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`Finance-tab ${tab === t.id ? 'Finance-tab--active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <div className="Finance-body">
        {loading && tab !== 'ledger' && <div className="Finance-loading">Загрузка…</div>}

        {tab === 'overview' && (
          <PaymentOverview refreshKey={items} onEdit={id => {const item = itemsById[id]; if (item) setModal({type:'item-edit', item});}} onTopup={accountId => setModal({type:'ledger-create',accountId})} />
        )}

        {tab === 'accounts' && (
          <AccountsTab
            accounts={accounts}
            items={items}
            onCreate={() => setModal({ type: 'account-create' })}
            onEdit={(a) => setModal({ type: 'account-edit', account: a })}
            onDelete={(a) => setModal({ type: 'account-delete', account: a })}
            onTopup={(id) => setModal({ type: 'ledger-create', accountId: id })}
          />
        )}

        {tab === 'items' && (
          <ItemsTab
            items={filteredItems}
            accounts={accounts}
            accountsById={accountsById}
            accountFilter={itemsAccountFilter}
            onAccountFilter={setItemsAccountFilter}
            groups={itemGroups}
            groupFilter={itemsGroupFilter}
            onGroupFilter={setItemsGroupFilter}
            showDisabled={showDisabledItems}
            onShowDisabled={setShowDisabledItems}
            onCreate={() => setModal({ type: 'item-create', accountId: itemsAccountFilter || undefined })}
            onEdit={(item) => setModal({ type: 'item-edit', item })}
            onDisable={(item) => setModal({ type: 'item-disable', item })}
            onDelete={(item) => setModal({ type: 'item-delete', item })}
            onTopup={(item) => setModal({ type: 'ledger-create', accountId: item.account_id, expenseItemId: item.id })}
          />
        )}

        {tab === 'ledger' && (
          <LedgerTab
            ledger={ledger}
            accounts={accounts}
            itemsById={itemsById}
            start={ledgerStart}
            end={ledgerEnd}
            accountFilter={ledgerAccountFilter}
            onStart={setLedgerStart}
            onEnd={setLedgerEnd}
            onAccountFilter={setLedgerAccountFilter}
            onApply={loadLedger}
            onCreate={() => setModal({ type: 'ledger-create' })}
            onEdit={(entry) => setModal({ type: 'ledger-edit', entry })}
            onDelete={(entry) => setModal({ type: 'ledger-delete', entry })}
          />
        )}

        {tab === 'occurrences' && (
          <OccurrencesTab
            occurrences={occurrences}
            onResolve={(occurrence) => setModal({ type: 'occurrence-resolve', occurrence })}
          />
        )}
      </div>

      {modal?.type === 'account-create' && (
        <AccountFormModal
          title="Новый счёт"
          onClose={() => setModal(null)}
          onSubmit={(body) => runAction(() => financeApi.createAccount(body), 'Счёт создан')}
        />
      )}
      {modal?.type === 'account-edit' && (
        <AccountFormModal
          title="Редактировать счёт"
          initial={modal.account}
          onClose={() => setModal(null)}
          onSubmit={(body) => runAction(
            () => financeApi.updateAccount(modal.account.id, body),
            'Счёт сохранён'
          )}
        />
      )}
      {modal?.type === 'account-delete' && (
        <ConfirmModal
          title="Удалить счёт?"
          message={`Счёт «${modal.account.name || '#' + modal.account.id}» и все связанные объекты затрат и проводки будут удалены безвозвратно.`}
          confirmLabel="Удалить"
          danger
          onClose={() => setModal(null)}
          onConfirm={() => runAction(
            () => financeApi.deleteAccount(modal.account.id),
            'Счёт удалён'
          )}
        />
      )}
      {modal?.type === 'item-create' && (
        <ItemFormModal
          title="Новый регулярный платёж"
          accounts={accounts}
          defaultAccountId={modal.accountId}
          onClose={() => setModal(null)}
          onSubmit={(body) => runAction(() => financeApi.createItem(body), 'Объект создан')}
        />
      )}
      {modal?.type === 'item-edit' && (
        <ItemFormModal
          title="Редактировать регулярный платёж"
          accounts={accounts}
          initial={modal.item}
          onClose={() => setModal(null)}
          onSubmit={(body) => runAction(
            () => financeApi.updateItem(modal.item.id, body),
            'Объект сохранён'
          )}
        />
      )}
      {modal?.type === 'item-disable' && (
        <ConfirmModal
          title="Архивировать платёж?"
          message={`«${modal.item.name}» будет отключён; событие в календаре удалится.`}
          confirmLabel="Архивировать"
          onClose={() => setModal(null)}
          onConfirm={() => runAction(
            () => financeApi.disableItem(modal.item.id),
            'Платёж архивирован'
          )}
        />
      )}
      {modal?.type === 'ledger-create' && (
        <LedgerFormModal
          title="Новая проводка"
          accounts={accounts}
          items={enabledItems}
          defaultAccountId={modal.accountId}
          defaultExpenseItemId={modal.expenseItemId}
          onClose={() => setModal(null)}
          onSubmit={(body) => runAction(() => financeApi.createLedgerEntry(body), 'Проводка добавлена')}
        />
      )}
      {modal?.type === 'ledger-edit' && (
        <LedgerFormModal
          title="Редактировать проводку"
          accounts={accounts}
          items={enabledItems}
          initial={modal.entry}
          editOnly
          onClose={() => setModal(null)}
          onSubmit={(body) => runAction(
            () => financeApi.updateLedgerEntry(modal.entry.id, body),
            'Проводка сохранена'
          )}
        />
      )}
      {modal?.type === 'ledger-delete' && (
        <ConfirmModal
          title="Удалить проводку?"
          message="Проводка будет удалена; баланс счёта пересчитается."
          confirmLabel="Удалить"
          danger
          onClose={() => setModal(null)}
          onConfirm={() => runAction(
            () => financeApi.deleteLedgerEntry(modal.entry.id),
            'Проводка удалена'
          )}
        />
      )}
      {modal?.type === 'item-delete' && (
        <ConfirmModal
          title="Удалить объект без истории?"
          message={`«${modal.item.name}» будет удалён окончательно. Если по нему уже были проводки, система сохранит историю и предложит архивирование.`}
          confirmLabel="Удалить"
          danger
          onClose={() => setModal(null)}
          onConfirm={() => runAction(
            () => financeApi.deleteItem(modal.item.id),
            'Объект удалён'
          )}
        />
      )}
      {modal?.type === 'occurrence-resolve' && (
        <OccurrenceResolveModal
          occurrence={modal.occurrence}
          onClose={() => setModal(null)}
          onSubmit={(body) => runAction(
            () => financeApi.resolveOccurrence(
              modal.occurrence.id,
              body,
              { revise: modal.occurrence.status !== 'pending' }
            ),
            'Платёж сверён'
          )}
        />
      )}
    </div></FinanceFeedbackContext.Provider>
  );
}


function AccountsTab({ accounts, items, onCreate, onEdit, onDelete, onTopup }) {
  const countByAccount = useMemo(() => {
    const m = {};
    items.forEach((i) => {
      if (!i.enabled) return;
      m[i.account_id] = (m[i.account_id] || 0) + 1;
    });
    return m;
  }, [items]);

  return (
    <section className="Finance-panel">
      <div className="Finance-panelToolbar">
        <h3>Счета</h3>
        <button type="button" className="Finance-btn" onClick={onCreate}>+ Счёт</button>
      </div>
      {accounts.length === 0 ? (
        <p className="Finance-empty">Нет счетов.</p>
      ) : (
        <div className="Finance-tableWrap">
          <table className="Finance-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Валюта</th>
                <th>Баланс</th>
                <th>Объектов</th>
                <th>Прогноз</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id}>
                  <td data-label="Название">{a.name || `Счёт #${a.id}`}</td>
                  <td data-label="Валюта">{a.currency}</td>
                  <td data-label="Баланс" className="Finance-num">{formatMoney(a.balance, a.currency)}</td>
                  <td data-label="Объектов">{countByAccount[a.id] || 0}</td>
                  <td data-label="Прогноз">
                    {a.first_insufficient_date ? (
                      <span className="Finance-warn">{formatDate(a.first_insufficient_date)}</span>
                    ) : '—'}
                  </td>
                  <td data-label="" className="Finance-actionsCell"><details className="Finance-actionsMenu"><summary>Действия</summary><div>
                    <button type="button" className="Finance-linkBtn" onClick={() => onTopup(a.id)}>Пополнить</button>
                    <button type="button" className="Finance-linkBtn" onClick={() => onEdit(a)}>Изменить</button>
                    <button type="button" className="Finance-linkBtn Finance-linkBtn--danger" onClick={() => onDelete(a)}>Удалить</button>
                  </div></details></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ItemsTab({
  items,
  accounts,
  accountsById,
  accountFilter,
  onAccountFilter,
  groups,
  groupFilter,
  onGroupFilter,
  showDisabled,
  onShowDisabled,
  onCreate,
  onEdit,
  onDisable,
  onDelete,
  onTopup,
}) {
  const groupedItems = useMemo(() => {
    const result = new Map();
    items.forEach((item) => {
      const name = item.group_name || 'Без группы';
      if (!result.has(name)) result.set(name, []);
      result.get(name).push(item);
    });
    return [...result.entries()].sort(([a], [b]) => {
      if (a === 'Без группы') return 1;
      if (b === 'Без группы') return -1;
      return a.localeCompare(b, 'ru');
    });
  }, [items]);
  return (
    <section className="Finance-panel">
      <div className="Finance-panelToolbar">
        <h3>Регулярные платежи</h3>
        <div className="Finance-filters">
          <label className="Finance-filter">
            Счёт
            <select value={accountFilter} onChange={(e) => onAccountFilter(e.target.value)}>
              <option value="">Все</option>
              {accounts.map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name || `#${a.id}`}
                </option>
              ))}
            </select>
          </label>
          <label className="Finance-filter">
            Группа
            <select value={groupFilter} onChange={(e) => onGroupFilter(e.target.value)}>
              <option value="">Все</option>
              {groups.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label className="Finance-filter Finance-filter--check">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => onShowDisabled(e.target.checked)}
            />
            Показать отключённые
          </label>
        </div>
        <button type="button" className="Finance-btn" onClick={onCreate}>+ Платёж</button>
      </div>
      {items.length === 0 ? (
        <p className="Finance-empty">Нет регулярных платежей.</p>
      ) : (
        <div className="Finance-tableWrap">
          <table className="Finance-table">
            <thead>
              <tr>
                <th>Название</th>
                <th>Счёт</th>
                <th>Расписание</th>
                <th>Сумма</th>
                <th>Баланс предоплаты</th>
                <th>Следующая дата</th>
                <th>Статус</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {groupedItems.map(([group, rows]) => (
                <React.Fragment key={group}>
                  <tr className="Finance-groupRow"><td colSpan={8}>{group} <span>{rows.length}</span></td></tr>
                  {rows.map((r) => (
                <tr key={r.id} className={r.enabled ? '' : 'Finance-row--disabled'}>
                  <td data-label="Название">
                    <strong>{r.name}</strong>
                    <div className="Finance-muted">{r.payment_type === 'prepaid' ? 'Предоплаченный баланс' : 'Обязательный платёж'}</div>
                    {r.description && <div className="Finance-muted">{r.description}</div>}
                  </td>
                  <td data-label="Счёт">{accountsById[r.account_id]?.name || `#${r.account_id}`}</td>
                  <td data-label="Расписание">{r.schedule_display || '—'}</td>
                  <td data-label="Сумма" className="Finance-num">{r.amount}</td>
                  <td data-label="Баланс предоплаты" className="Finance-num">
                    {r.payment_type === 'prepaid' ? r.balance : '—'}
                  </td>
                  <td data-label="Следующая дата">{r.reminder_anchor_date ? formatDate(r.reminder_anchor_date) : '—'}</td>
                  <td data-label="Статус">{r.enabled ? 'Вкл' : 'Выкл'}</td>
                  <td data-label="" className="Finance-actionsCell"><details className="Finance-actionsMenu"><summary>Действия</summary><div>
                    {r.payment_type === 'prepaid' && <button type="button" className="Finance-linkBtn" onClick={() => onTopup(r)}>Пополнить</button>}
                    <button type="button" className="Finance-linkBtn" onClick={() => onEdit(r)}>Изменить</button>
                    {r.enabled && (
                      <button type="button" className="Finance-linkBtn Finance-linkBtn--danger" onClick={() => onDisable(r)}>
                        Архивировать
                      </button>
                    )}
                    <button type="button" className="Finance-linkBtn Finance-linkBtn--danger" onClick={() => onDelete(r)}>
                      Удалить
                    </button>
                  </div></details></td>
                </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const OUTCOME_LABELS = {
  confirmed: 'Подтверждён',
  paid_elsewhere: 'Оплачен с другой карты',
  skipped: 'Пропущен',
  corrected: 'Сумма скорректирована',
};

function OccurrencesTab({ occurrences, onResolve }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [itemFilter, setItemFilter] = useState('');
  const itemOptions = useMemo(() => {
    const names = new Map();
    occurrences.forEach((row) => names.set(String(row.expense_item_id), row.name));
    return Array.from(names.entries()).sort((a, b) => a[1].localeCompare(b[1], 'ru'));
  }, [occurrences]);
  const visible = useMemo(() => occurrences.filter((row) => {
    if (statusFilter !== 'all' && row.status !== statusFilter) return false;
    if (itemFilter && String(row.expense_item_id) !== itemFilter) return false;
    return true;
  }), [occurrences, statusFilter, itemFilter]);
  return (
    <section className="Finance-panel">
      <div className="Finance-panelToolbar Finance-panelToolbar--wrap">
        <h3>История платежей</h3>
        <span className="Finance-muted">{visible.length} из {occurrences.length}</span>
        <div className="Finance-filters">
          <label className="Finance-filter">Статус
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">Все</option><option value="pending">Ожидают сверки</option><option value="answered">Сверены</option>
            </select>
          </label>
          <label className="Finance-filter">Платёж
            <select value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
              <option value="">Все</option>{itemOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
        </div>
      </div>
      {visible.length === 0 ? (
        <p className="Finance-empty">Сроков платежей пока нет.</p>
      ) : (
        <div className="Finance-tableWrap">
          <table className="Finance-table">
            <thead><tr><th>Срок</th><th>Объект</th><th>План</th><th>Факт</th><th>Статус</th><th /></tr></thead>
            <tbody>
              {visible.map((row) => (
                <tr key={row.id}>
                  <td data-label="Срок">{formatDate(row.due_date)}</td>
                  <td data-label="Объект">{row.name}</td>
                  <td data-label="План">{formatMoney(row.scheduled_amount, row.currency)}</td>
                  <td data-label="Факт">{row.actual_amount == null ? '—' : formatMoney(row.actual_amount, row.currency)}</td>
                  <td data-label="Статус">
                    <span className={`Finance-status Finance-status--${row.status === 'pending' ? 'pending' : row.outcome}`}>
                      {row.status === 'pending' ? 'Ожидает сверки' : (OUTCOME_LABELS[row.outcome] || row.outcome)}
                    </span>
                    {row.user_note && <div className="Finance-muted">{row.user_note}</div>}
                  </td>
                  <td data-label=""><button type="button" className="Finance-linkBtn" onClick={() => onResolve(row)}>{row.status === 'pending' ? 'Сверить' : 'Изменить'}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function LedgerTab({
  ledger,
  accounts,
  itemsById,
  start,
  end,
  accountFilter,
  onStart,
  onEnd,
  onAccountFilter,
  onApply,
  onCreate,
  onEdit,
  onDelete,
}) {
  return (
    <section className="Finance-panel">
      <div className="Finance-panelToolbar Finance-panelToolbar--wrap">
        <h3>Журнал проводок</h3>
        <div className="Finance-filters">
          <label className="Finance-filter">
            С
            <input type="date" value={start} onChange={(e) => onStart(e.target.value)} />
          </label>
          <label className="Finance-filter">
            По
            <input type="date" value={end} onChange={(e) => onEnd(e.target.value)} />
          </label>
          <label className="Finance-filter">
            Счёт
            <select value={accountFilter} onChange={(e) => onAccountFilter(e.target.value)}>
              <option value="">Все</option>
              {accounts.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name || `#${a.id}`}</option>
              ))}
            </select>
          </label>
          <button type="button" className="Finance-btn Finance-btn--ghost" onClick={onApply}>Применить</button>
        </div>
        <button type="button" className="Finance-btn" onClick={onCreate}>+ Проводка</button>
      </div>
      {ledger.length === 0 ? (
        <p className="Finance-empty">Нет проводок за период.</p>
      ) : (
        <div className="Finance-tableWrap">
          <table className="Finance-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Счёт</th>
                <th>Тип</th>
                <th>Объект</th>
                <th>Сумма</th>
                <th>Примечание</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ledger.map((p) => {
                const mutable = MUTABLE_KINDS.has(p.kind);
                const acc = accounts.find((a) => a.id === p.account_id);
                return (
                  <tr key={p.id}>
                    <td data-label="Дата">{formatDate(p.date)}</td>
                    <td data-label="Счёт">{acc?.name || `#${p.account_id}`}</td>
                    <td data-label="Тип">{KIND_LABELS[p.kind] || p.kind}</td>
                    <td data-label="Объект">
                      {p.expense_item_id
                        ? (itemsById[p.expense_item_id]?.name || `ID ${p.expense_item_id}`)
                        : '—'}
                    </td>
                    <td data-label="Сумма" className={`Finance-num ${Number(p.amount) < 0 ? 'Finance-num--neg' : 'Finance-num--pos'}`}>
                      {p.amount}
                    </td>
                    <td data-label="Примечание" className="Finance-note">{p.note || '—'}</td>
                    <td data-label="" className="Finance-actionsCell"><details className="Finance-actionsMenu"><summary>Действия</summary><div>
                      {mutable ? (
                        <>
                          <button type="button" className="Finance-linkBtn" onClick={() => onEdit(p)}>Изменить</button>
                          <button type="button" className="Finance-linkBtn Finance-linkBtn--danger" onClick={() => onDelete(p)}>Удалить</button>
                        </>
                      ) : (
                        <span className="Finance-muted">авто</span>
                      )}
                    </div></details></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AccountFormModal({ title, initial, onClose, onSubmit }) {
  const [name, setName] = useState(initial?.name || '');
  const [currency, setCurrency] = useState(initial?.currency || 'RUB');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({ name, currency });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form className="Finance-form" onSubmit={handleSubmit}>
        <label className="Finance-field">
          <span className="Finance-label">Название</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Основной" />
        </label>
        <label className="Finance-field">
          <span className="Finance-label">Валюта</span>
          <input value={currency} onChange={(e) => setCurrency(e.target.value)} maxLength={8} />
        </label>
        <div className="Finance-formActions">
          <button type="button" className="Finance-btn Finance-btn--ghost" onClick={onClose}>Отмена</button>
          <button type="submit" className="Finance-btn" disabled={saving}>{saving ? '…' : 'Сохранить'}</button>
        </div>
      </form>
    </Modal>
  );
}

function ItemFormModal({ title, accounts, initial, defaultAccountId, onClose, onSubmit }) {
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [groupName, setGroupName] = useState(initial?.group_name || '');
  const [amount, setAmount] = useState(initial?.amount_per_period || initial?.amount || '');
  const [paymentType, setPaymentType] = useState(initial?.payment_type || 'fixed');
  const [accountId, setAccountId] = useState(
    initial?.account_id ? String(initial.account_id) : (defaultAccountId ? String(defaultAccountId) : '')
  );
  const [newAccountName, setNewAccountName] = useState('');
  const [validFrom, setValidFrom] = useState(initial?.valid_from || todayYmd());
  const [validTo, setValidTo] = useState(initial?.valid_to || '');
  const [enabled, setEnabled] = useState(initial?.enabled !== false);
  const [initialBalance, setInitialBalance] = useState('');
  const [schedule, setSchedule] = useState(() => scheduleFromApi(initial?.schedule));
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);
  const useNewAccount = !accountId && !isEdit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    const body = {
      name: name.trim(),
      description,
      group_name: groupName.trim(),
      amount_per_period: amount,
      payment_type: paymentType,
      schedule: buildSchedulePayload(schedule),
      valid_from: validFrom,
      valid_to: validTo || null,
      enabled,
    };
    if (useNewAccount) {
      body.new_account_name = newAccountName.trim() || `${name.trim()} (счёт)`;
    } else if (accountId) {
      body.account_id = Number(accountId);
    }
    if (!isEdit && paymentType === 'prepaid' && initialBalance !== '') {
      body.initial_balance = initialBalance;
    }
    try {
      await onSubmit(body);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose} wide>
      <form className="Finance-form" onSubmit={handleSubmit}>
        <label className="Finance-field">
          <span className="Finance-label">Тип платежа</span>
          <select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
            <option value="fixed">Обязательный платёж</option>
            <option value="prepaid">Предоплаченный баланс</option>
          </select>
        </label>
        <p className="Finance-hint">{paymentType === 'fixed' ? 'Известная сумма в определённую дату: квартира, музыка, подписка.' : 'Пополняемый счёт: укажите расход за период и остаток, чтобы рассчитать дату исчерпания.'}</p>
        <div className="Finance-formGrid">
          <label className="Finance-field">
            <span className="Finance-label">Название *</span>
            <input value={name} onChange={(e) => setName(e.target.value)} required />
          </label>
          <label className="Finance-field">
            <span className="Finance-label">Группа</span>
            <input value={groupName} onChange={(e) => setGroupName(e.target.value)} maxLength={100} placeholder="Например, REG.RU" />
          </label>
          <label className="Finance-field">
            <span className="Finance-label">Сумма за период *</span>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required />
          </label>
        </div>

        {!isEdit && paymentType === 'prepaid' && (
          <label className="Finance-field">
            <span className="Finance-label">Счёт</span>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">— Создать новый —</option>
              {accounts.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name || `#${a.id}`} ({a.balance})</option>
              ))}
            </select>
          </label>
        )}
        {!isEdit && paymentType === 'prepaid' && useNewAccount && (
          <label className="Finance-field">
            <span className="Finance-label">Имя нового счёта</span>
            <input value={newAccountName} onChange={(e) => setNewAccountName(e.target.value)} />
          </label>
        )}
        {isEdit && paymentType === 'prepaid' && (
          <label className="Finance-field">
            <span className="Finance-label">Счёт</span>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)} required>
              {accounts.map((a) => (
                <option key={a.id} value={String(a.id)}>{a.name || `#${a.id}`}</option>
              ))}
            </select>
          </label>
        )}
        <label className="Finance-field">
          <span className="Finance-label">Описание</span>
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
        </label>
        <ScheduleForm value={schedule} onChange={setSchedule} />
        <div className="Finance-formGrid">
          <label className="Finance-field">
            <span className="Finance-label">Действует с *</span>
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} required />
          </label>
          <label className="Finance-field">
            <span className="Finance-label">Действует до</span>
            <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} />
          </label>
        </div>
        {isEdit && (
          <label className="Finance-field Finance-field--check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Включено
          </label>
        )}
        {!isEdit && paymentType === 'prepaid' && (
          <label className="Finance-field">
            <span className="Finance-label">Начальный остаток (опционально)</span>
            <input type="number" step="0.01" value={initialBalance} onChange={(e) => setInitialBalance(e.target.value)} />
          </label>
        )}
        <div className="Finance-formActions">
          <button type="button" className="Finance-btn Finance-btn--ghost" onClick={onClose}>Отмена</button>
          <button type="submit" className="Finance-btn" disabled={saving}>{saving ? '…' : 'Сохранить'}</button>
        </div>
      </form>
    </Modal>
  );
}

function LedgerFormModal({
  title,
  accounts,
  items,
  initial,
  defaultAccountId,
  defaultExpenseItemId,
  editOnly,
  onClose,
  onSubmit,
}) {
  const isEdit = Boolean(initial?.id);
  const [accountId, setAccountId] = useState(
    String(initial?.account_id || defaultAccountId || accounts[0]?.id || '')
  );
  const [expenseItemId, setExpenseItemId] = useState(
    initial?.expense_item_id ? String(initial.expense_item_id) : (defaultExpenseItemId ? String(defaultExpenseItemId) : '')
  );
  const [kind, setKind] = useState(initial?.kind || 'topup');
  const [signedAmount, setSignedAmount] = useState(initial?.signed_amount || initial?.amount || '');
  const [entryDate, setEntryDate] = useState(initial?.date || todayYmd());
  const [note, setNote] = useState(initial?.note || '');
  const [saving, setSaving] = useState(false);

  const itemsOnAccount = items.filter((i) => String(i.account_id) === String(accountId));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      account_id: Number(accountId),
      signed_amount: signedAmount,
      note,
      entry_date: entryDate,
    };
    if (!isEdit) body.kind = kind;
    if (expenseItemId) body.expense_item_id = Number(expenseItemId);
    try {
      await onSubmit(body);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={title} onClose={onClose}>
      <form className="Finance-form" onSubmit={handleSubmit}>
        {!isEdit && (
          <label className="Finance-field">
            <span className="Finance-label">Тип</span>
            <select value={kind} onChange={(e) => {
              const nextKind = e.target.value;
              setKind(nextKind);
              const n = Number(signedAmount);
              if (Number.isFinite(n) && n !== 0) {
                if (nextKind === 'debit_confirmed') setSignedAmount(String(-Math.abs(n)));
                if (nextKind === 'topup') setSignedAmount(String(Math.abs(n)));
              }
            }}>
              <option value="topup">Пополнение</option>
              <option value="adjustment">Корректировка</option>
              <option value="debit_confirmed">Списание (факт)</option>
            </select>
          </label>
        )}
        <label className="Finance-field">
          <span className="Finance-label">Счёт</span>
          <select
            value={accountId}
            onChange={(e) => { setAccountId(e.target.value); setExpenseItemId(''); }}
            disabled={isEdit}
            required
          >
            {accounts.map((a) => (
              <option key={a.id} value={String(a.id)}>{a.name || `#${a.id}`}</option>
            ))}
          </select>
        </label>
        <label className="Finance-field">
          <span className="Finance-label">Регулярный платёж</span>
          <select value={expenseItemId} onChange={(e) => setExpenseItemId(e.target.value)}>
            <option value="">— не привязан —</option>
            {itemsOnAccount.map((i) => (
              <option key={i.id} value={String(i.id)}>{i.name}</option>
            ))}
          </select>
        </label>
        <label className="Finance-field">
          <span className="Finance-label">Сумма {kind === 'debit_confirmed' ? '(списание)' : kind === 'topup' ? '(пополнение)' : '(+ / −)'}</span>
          <input
            type="number"
            step="0.01"
            value={signedAmount}
            onChange={(e) => setSignedAmount(e.target.value)}
            required
          />
        </label>
        <label className="Finance-field">
          <span className="Finance-label">Дата</span>
          <input type="date" value={entryDate} onChange={(e) => setEntryDate(e.target.value)} required />
        </label>
        <label className="Finance-field">
          <span className="Finance-label">Примечание</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        {editOnly && (
          <p className="Finance-hint">Автоматические списания защищены. Ручные пополнения, списания и корректировки можно изменять.</p>
        )}
        <div className="Finance-formActions">
          <button type="button" className="Finance-btn Finance-btn--ghost" onClick={onClose}>Отмена</button>
          <button type="submit" className="Finance-btn" disabled={saving}>{saving ? '…' : 'Сохранить'}</button>
        </div>
      </form>
    </Modal>
  );
}

function OccurrenceResolveModal({ occurrence, onClose, onSubmit }) {
  const [outcome, setOutcome] = useState(occurrence.outcome || 'confirmed');
  const [actualAmount, setActualAmount] = useState(occurrence.actual_amount || occurrence.scheduled_amount || '');
  const [userNote, setUserNote] = useState(occurrence.user_note || '');
  const [saving, setSaving] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSubmit({
        outcome,
        actual_amount: outcome === 'corrected' ? actualAmount : undefined,
        user_note: userNote,
      });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Modal title={`Сверить: ${occurrence.name}`} onClose={onClose}>
      <form className="Finance-form" onSubmit={handleSubmit}>
        <p className="Finance-hint">План: {formatMoney(occurrence.scheduled_amount, occurrence.currency)} · {formatDate(occurrence.due_date)}</p>
        <label className="Finance-field"><span className="Finance-label">Результат</span>
          <select value={outcome} onChange={(e) => setOutcome(e.target.value)}>
            <option value="confirmed">Списано как запланировано</option>
            <option value="paid_elsewhere">Оплачено с другой карты</option>
            <option value="skipped">Платёж пропущен</option>
            <option value="corrected">Списана другая сумма</option>
          </select>
        </label>
        {outcome === 'corrected' && <label className="Finance-field"><span className="Finance-label">Фактическая сумма</span><input type="number" min="0" step="0.01" value={actualAmount} onChange={(e) => setActualAmount(e.target.value)} required /></label>}
        <label className="Finance-field"><span className="Finance-label">Комментарий</span><input value={userNote} onChange={(e) => setUserNote(e.target.value)} /></label>
        <div className="Finance-formActions"><button type="button" className="Finance-btn Finance-btn--ghost" onClick={onClose}>Отмена</button><button type="submit" className="Finance-btn" disabled={saving}>{saving ? '…' : 'Сохранить'}</button></div>
      </form>
    </Modal>
  );
}

function ConfirmModal({ title, message, confirmLabel, danger, onClose, onConfirm }) {
  const [busy, setBusy] = useState(false);
  const handle = async () => {
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={title} onClose={onClose}>
      <p className="Finance-confirmText">{message}</p>
      <div className="Finance-formActions">
        <button type="button" className="Finance-btn Finance-btn--ghost" onClick={onClose}>Отмена</button>
        <button
          type="button"
          className={`Finance-btn ${danger ? 'Finance-btn--danger' : ''}`}
          onClick={handle}
          disabled={busy}
        >
          {busy ? '…' : confirmLabel}
        </button>
      </div>
    </Modal>
  );
}
